// src/utils/authUtils.js

const LOGIN_DETAILS_KEY = "loginDetails";

// Single read/write point for the loginDetails blob so every helper below
// stays in sync and we don't repeat JSON.parse/localStorage boilerplate.
const getLoginDetails = () => {
  try {
    return JSON.parse(localStorage.getItem(LOGIN_DETAILS_KEY)) || null;
  } catch (error) {
    console.error("Error parsing loginDetails from localStorage", error);
    return null;
  }
};

const saveLoginDetails = (updates) => {
  const current = getLoginDetails() || {};
  const updated = { ...current, ...updates };
  localStorage.setItem(LOGIN_DETAILS_KEY, JSON.stringify(updated));
  return updated;
};

export const getAuthToken = () => getLoginDetails()?.token || null;

export const getRefreshToken = () => getLoginDetails()?.refreshToken || null;

export const getSpocStatus = () => {
  const spocStatus = getLoginDetails()?.userDetails?.spocStatus;
  return spocStatus === true || spocStatus === "true";
};

// Merge new tokens into the existing loginDetails so userDetails etc. are kept.
export const setTokens = ({ token, refreshToken }) =>
  saveLoginDetails({
    ...(token ? { token } : {}),
    ...(refreshToken ? { refreshToken } : {}),
  });

// Decodes a JWT and returns its expiry time in milliseconds (from the `exp`
// claim), or null if the token isn't a decodable JWT.
export const getTokenExpiryMs = (token) => {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const { exp } = JSON.parse(atob(base64));
    return exp ? exp * 1000 : null;
  } catch {
    return null;
  }
};

// Frontend-only refresh: no backend call. When the access token expires, the
// refreshToken already stored in localStorage is promoted to be the active
// token and the next proactive refresh is scheduled against it.
// Returns the new active token, or null when there's nothing left to fall
// back to (caller should log the user out in that case).
export const refreshAuthToken = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  // Already running on the refresh token and it's still invalid -> give up.
  if (getAuthToken() === refreshToken) return null;

  setTokens({ token: refreshToken });
  scheduleProactiveRefresh();
  return refreshToken;
};

// Single-flight guard: many API calls can 401 at the same time, but only one
// refresh should ever run at a time. Concurrent callers share the same promise.
let refreshPromise = null;
export const getValidRefreshedToken = () => {
  if (!refreshPromise) {
    refreshPromise = refreshAuthToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

// Proactive refresh: rather than waiting for a request to 401, read the active
// token's real expiry (JWT `exp`) and refresh right when it expires, even if
// the app is idle with no requests in flight.
const REFRESH_LEAD_MS = 2000;
let refreshTimer = null;

export const clearProactiveRefresh = () => {
  clearTimeout(refreshTimer);
  refreshTimer = null;
};

export const scheduleProactiveRefresh = () => {
  clearProactiveRefresh();

  const token = getAuthToken();
  const expiryMs = token && getTokenExpiryMs(token);
  if (!expiryMs) return;

  const delay = Math.max(expiryMs - Date.now() - REFRESH_LEAD_MS, 0);
  refreshTimer = setTimeout(async () => {
    const newToken = await getValidRefreshedToken();
    if (!newToken) {
      await callLogoutApi();
      handleUnauthorizedError();
    }
    // On success, refreshAuthToken() already scheduled the next refresh.
  }, delay);
};

export const handleUnauthorizedError = () => {
  clearProactiveRefresh();
  localStorage.removeItem(LOGIN_DETAILS_KEY);
  localStorage.removeItem("isAuthenticated");
  localStorage.removeItem("microsoftLogin");
  localStorage.removeItem("user_email");
  window.location.href = "./";
};

export const callLogoutApi = async () => {
  const token = getAuthToken();
  if (!token) return;
  try {
    await fetch(`${process.env.REACT_APP_DEV_API_URL}users/auth/logout`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
  } catch {
    // Proceed with logout regardless of network errors.
  }
};
